import React from 'react';
import { sustainabilityItems, sustainabilityStatement, IMAGES } from '../mock';
import { Droplets, Recycle, RotateCcw, TrendingDown, Gem } from 'lucide-react';
import { Reveal, StaggerGroup, StaggerItem } from './Reveal';
import { motion } from 'framer-motion';

const iconMap = { Droplets, Recycle, RotateCcw, TrendingDown, Gem };

const Sustainability = () => {
  return (
    <section id="sustainability" className="relative py-24 lg:py-32 bg-[#fafaf7] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-16">
        <div className="relative overflow-hidden min-h-[480px]">
          <motion.img
            initial={{ scale: 1.15 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 2.5, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true }}
            src={IMAGES.earthSustain}
            alt="Green earth"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/95 via-emerald-900/85 to-emerald-800/50" />
          <div className="absolute inset-0 grain-texture pointer-events-none opacity-40" />

          <div className="relative p-10 lg:p-16">
            <Reveal>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-[1px] bg-[#c4a24a]" />
                <span className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">
                  06 — Sustainability
                </span>
              </div>
              <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
                <h2
                  className="lg:col-span-7 text-[42px] lg:text-[60px] xl:text-[68px] leading-[1.03] font-medium text-white text-balance"
                  style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.03em' }}
                >
                  Caring for <span className="italic text-[#c4a24a] font-light">tomorrow.</span>
                </h2>
                <div className="lg:col-span-5">
                  <p className="text-[17px] lg:text-[19px] leading-[1.65] text-white/90 font-light italic" style={{ fontFamily: 'Fraunces, serif' }}>
                    &ldquo;Weight, permanence, and purpose. Encased in heavyweight, infinitely recyclable vessels and crafted through responsible water management, true luxury leaves a lighter footprint.&rdquo;
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-8 h-[1px] bg-[#c4a24a]" />
                    <span className="text-[10px] tracking-[0.28em] text-[#c4a24a] uppercase font-medium">
                      Our sustainability philosophy
                    </span>
                  </div>
                </div>
              </div>
            </Reveal>

            <StaggerGroup className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 max-w-4xl" delay={0.1}>
              {sustainabilityItems.map((item, idx) => {
                const Icon = iconMap[item.icon];
                return (
                  <StaggerItem key={idx}>
                    <div className="flex flex-col items-start text-left group cursor-default">
                      <div className="relative mb-4">
                        <div className="absolute inset-0 rounded-full bg-[#c4a24a]/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="relative w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white group-hover:border-[#c4a24a] group-hover:scale-110 transition-all duration-500">
                          <Icon size={22} strokeWidth={1.4} />
                        </div>
                      </div>
                      <p className="text-[13px] font-medium text-white leading-[1.5] whitespace-pre-line">
                        {item.label}
                      </p>
                    </div>
                  </StaggerItem>
                );
              })}
            </StaggerGroup>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Sustainability;
